// src/app/components/DeviceTypePicker/DeviceTypeMenu.tsx
import React, { useMemo, useState } from "react";
import { Menu, TextInput, Stack, ScrollArea, Box, Text, Group, Badge } from "@mantine/core";
import { TbChevronDown } from "react-icons/tb";
import { IDeviceType } from "@backend/src/shared/schemas/schemas";
import { DEVICES_TYPES_SETTINGS } from "@multiconsult-group/multiplacer-core";
import { IDeviceCategoryEnum } from "@backend/src/shared/schemas/foundationSchema";

interface Props {
  deviceCategory: IDeviceCategoryEnum;
  deviceTypes: IDeviceType[];
  selectedType: IDeviceType | null; // 👈 NY
  disabled: boolean;
  onSelect: (dt: IDeviceType) => void;
}

export const DeviceTypeMenu: React.FC<Props> = ({
  deviceCategory,
  deviceTypes,
  selectedType,
  disabled,
  onSelect,
}) => {
  const [search, setSearch] = useState("");

  const filteredTypes = useMemo(() => {
    const s = search.toLowerCase();
    return deviceTypes.filter((dt) => {
      const code = (dt.compCode ?? "").toLowerCase();
      const name = (dt.typeName ?? "").toLowerCase();
      return code.includes(s) || name.includes(s);
    });
  }, [deviceTypes, search]);

  const settings = DEVICES_TYPES_SETTINGS[deviceCategory];
  const categoryLabel = settings?.label ?? deviceCategory;

  const selectedLabel = useMemo(() => {
    if (!selectedType) {
      return "Velg device-type";
    }
    const code = selectedType.compCode ?? "Ingen compcode";
    const name = selectedType.typeName ?? "";
    return name ? `${code} – ${name}` : code;
  }, [selectedType]);

  return (
    <Menu width={320} position="bottom-start" withinPortal disabled={disabled}>
      <Menu.Target>
        <Box
          p={6}
          px={10}
          style={{
            border: "1px solid #ccc",
            borderRadius: 6,
            cursor: "pointer",
            background: "#fff",
          }}
        >
          <Group justify="space-between" gap="xs" wrap="nowrap">
            <Stack gap={0} style={{ minWidth: 0, flex: 1 }}>
              <Text fw={500} size="sm" lineClamp={1}>
                {selectedLabel}
              </Text>
              <Group gap={6} align="center">
                <Text size="xs" c="dimmed" lineClamp={1}>
                  {categoryLabel}
                </Text>
              </Group>
            </Stack>
            <TbChevronDown size={16} />
          </Group>
        </Box>
      </Menu.Target>

      <Menu.Dropdown>
        <Stack gap={6} p="xs">
          <TextInput
            placeholder="Søk device-typer…"
            value={search}
            onChange={(e) => setSearch(e.currentTarget.value)}
            size="xs"
          />

          <ScrollArea.Autosize mah={260}>
            <Stack gap={4}>
              {filteredTypes.length === 0 && (
                <Text size="xs" c="dimmed">
                  Ingen treff
                </Text>
              )}

              {filteredTypes.map((dt) => (
                <Menu.Item key={dt._id as any} onClick={() => onSelect(dt)}>
                  <Stack gap={0}>
                    <Text fw={500} size="sm">
                      {dt.compCode ?? "Ingen compcode"}
                    </Text>
                    <Text size="xs" c="dimmed" lineClamp={2}>
                      {dt.typeName}
                    </Text>
                  </Stack>
                </Menu.Item>
              ))}
            </Stack>
          </ScrollArea.Autosize>
        </Stack>
      </Menu.Dropdown>
    </Menu>
  );
};
